// Copyright 2016 Sorama B.V.

import { bindable, bindingMode, customElement, inject } from "aurelia-framework";

@customElement("editable-text")
@inject(Element)
export class EditableText {
    private static acceptEventName = "accept";
    private static cancelEventName = "cancel";

    private element: Element;

    //#region Bindable Properties
    @bindable({ defaultBindingMode: bindingMode.twoWay }) value: string | number;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) placeholder = "Edit text...";
    @bindable({ defaultBindingMode: bindingMode.oneWay }) isEditable = true;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) isMultiline: boolean;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) contentType = "text";
    @bindable({ defaultBindingMode: bindingMode.oneWay }) unit: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) inputSuggestions: string[];
    @bindable({ defaultBindingMode: bindingMode.oneWay }) autoAccept = false;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) autoFocus = false;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) numberOfDigits: number;
    //#endregion

    //#region Fields
    private isEditing = false;
    private internalValue: string;
    //#endregion

    constructor(element: Element) {
        this.element = element;
    }

    public attached(): void {
        // Focus on the textbox in edit mode when autofocus is set
        if (this.autoFocus === true || (this.autoFocus as unknown as string) == "true") {
            this.edit();
        }
    }

    public inputFieldActive = function () {
        this.inputfield.focus();
    };

    private edit(): void {
        if (this.isEditable) {
            this.internalValue = this.value ? this.value.toString() : undefined;
            this.isEditing = true;
        }
    }

    private accept(): void {
        const oldValue = this.value;

        if (this.contentType === "number") {
            // Try parsing as a number
            const num = parseFloat(this.internalValue);
            if (isNaN(num) || num.toString() !== this.internalValue) {
                // Invalid input, don't accept
                return;
            }
            this.value = num;
        } else {
            this.value = this.internalValue;
        }

        this.isEditing = false;

        const acceptEvent = new CustomEvent(EditableText.acceptEventName, <CustomEventInit>{
            detail: {
                newValue: this.value,
                oldValue: oldValue,
            },
            bubbles: true,
        });

        this.element.dispatchEvent(acceptEvent);
    }

    private cancel(): void {
        this.internalValue = this.value ? this.value.toString() : undefined;
        this.isEditing = false;

        const cancelEvent = new CustomEvent(EditableText.cancelEventName, {
            bubbles: true,
        });

        this.element.dispatchEvent(cancelEvent);
    }

    private lostFocus() {
        if (<unknown>this.autoAccept == "true" || this.autoAccept === true) this.accept();
    }

    private isEditableChanged(newValue: boolean, oldValue: boolean): void {
        if (this.isEditing) {
            this.isEditing = false;
        }
    }
}
